import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAdminBusinesses,
  setBusinessStatus,
  setBusinessFlags,
  deleteBusiness,
  bulkBusinessAction,
  type BulkBusinessAction,
} from "@/services/adminService";
import type { AdminBusiness, AdminBusinessStatus } from "@/types/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import BusinessFormModal from "@/pages/portal/admin/components/BusinessFormModal";
import {
  adminCard,
  adminTh,
  adminTd,
  adminBtnGhost,
  adminBtnDanger,
  adminBtnPrimary,
  adminIconBtn,
  statusBadge,
  adminInput,
  adminLabel,
} from "@/pages/portal/admin/config/ui";

const STATUS_OPTIONS: { value: "all" | AdminBusinessStatus; label: string }[] = [
  { value: "all",       label: "All statuses" },
  { value: "active",    label: "Active" },
  { value: "pending",   label: "Pending" },
  { value: "rejected",  label: "Rejected" },
  { value: "suspended", label: "Suspended" },
];

interface PendingAction {
  type: "reject" | "delete";
  business: AdminBusiness;
}

export default function BusinessesSection() {
  const { accessToken } = useAuth();
  const { data, setData, loading, error, reload } = useAdminResource(
    () => getAdminBusinesses(accessToken),
    [] as AdminBusiness[],
    [accessToken],
  );

  const [search, setSearch]   = useState("");
  const [status, setStatus]   = useState<"all" | AdminBusinessStatus>("all");
  const [plan, setPlan]       = useState("all");
  const [busyId, setBusyId]   = useState<number | null>(null);
  const [action, setAction]   = useState<PendingAction | null>(null);
  const [reason, setReason]   = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState<BulkBusinessAction | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminBusiness | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((b) => {
      if (status !== "all" && b.status !== status) return false;
      if (plan !== "all" && b.planSlug !== plan) return false;
      if (!q) return true;
      return [b.name, b.ownerName, b.ownerEmail, b.categoryName, b.districtName]
        .join(" ").toLowerCase().includes(q);
    });
  }, [data, search, status, plan]);

  const visibleIds  = filtered.map((b) => b.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = visibleIds.some((id) => selected.has(id));

  function patch(id: number, updater: (b: AdminBusiness) => AdminBusiness) {
    setData((prev) => prev.map((b) => (b.id === id ? updater(b) : b)));
  }

  async function handleStatus(b: AdminBusiness, next: AdminBusinessStatus, note?: string) {
    setBusyId(b.id);
    try {
      await setBusinessStatus(accessToken, b.id, next, note);
      patch(b.id, (row) => ({ ...row, status: next }));
      setAction(null);
      setReason("");
    } finally {
      setBusyId(null);
    }
  }

  async function handleFlag(b: AdminBusiness, key: "isFeatured" | "isSponsored" | "isVerified") {
    const next = !b[key];
    patch(b.id, (row) => ({ ...row, [key]: next }));
    await setBusinessFlags(accessToken, b.id, { [key]: next });
  }

  async function handleDelete(b: AdminBusiness) {
    setBusyId(b.id);
    try {
      await deleteBusiness(accessToken, b.id);
      setData((prev) => prev.filter((row) => row.id !== b.id));
      setAction(null);
    } finally {
      setBusyId(null);
    }
  }

  function toggleRow(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      const everyVisibleSelected = visibleIds.every((id) => next.has(id));
      if (everyVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }

  async function runBulk(bulkAction: BulkBusinessAction) {
    const targetIds = new Set(selected);
    if (targetIds.size === 0) return;
    setBulkBusy(true);
    try {
      await bulkBusinessAction(accessToken, [...targetIds], bulkAction);
      setData((prev) => {
        if (bulkAction === "archive") {
          return prev.filter((row) => !targetIds.has(row.id));
        }
        return prev.map((row) => {
          if (!targetIds.has(row.id)) return row;
          if (bulkAction === "approve") return { ...row, status: "active" as AdminBusinessStatus };
          if (bulkAction === "feature") return { ...row, isFeatured: true };
          return { ...row, isFeatured: false };
        });
      });
      setSelected(new Set());
      setBulkConfirm(null);
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <AdminSearchInput value={search} onChange={setSearch} placeholder="Search businesses, owners…" />
        <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className="h-9 cursor-pointer rounded-lg border border-background-300 bg-background-100 px-3 text-xs text-foreground-700 outline-none focus:border-primary-400">
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={plan} onChange={(e) => setPlan(e.target.value)} className="h-9 cursor-pointer rounded-lg border border-background-300 bg-background-100 px-3 text-xs text-foreground-700 outline-none focus:border-primary-400">
          <option value="all">All plans</option>
          <option value="free">Free</option>
          <option value="business">Business</option>
          <option value="premium">Premium</option>
        </select>
        <span className="ml-auto text-xs text-foreground-500">
          {loading ? "Loading…" : `${filtered.length} of ${data.length} listings`}
        </span>
        <button type="button" onClick={() => setCreating(true)} className={adminBtnPrimary}>
          <i className="ri-add-line text-sm" /> Create listing
        </button>
      </div>

      {error && <AdminDataError message={error} onRetry={reload} />}

      {selected.size > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-primary-800">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-checkbox-multiple-line text-sm" />
            </span>
            {selected.size} selected
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" disabled={bulkBusy} onClick={() => runBulk("approve")} className={adminBtnGhost}>
              <i className="ri-checkbox-circle-line text-sm text-emerald-600" /> Approve
            </button>
            <button type="button" disabled={bulkBusy} onClick={() => runBulk("feature")} className={adminBtnGhost}>
              <i className="ri-star-line text-sm text-accent-600" /> Feature
            </button>
            <button type="button" disabled={bulkBusy} onClick={() => runBulk("unfeature")} className={adminBtnGhost}>
              <i className="ri-star-off-line text-sm" /> Unfeature
            </button>
            <button type="button" disabled={bulkBusy} onClick={() => setBulkConfirm("archive")} className={adminBtnDanger}>
              <i className="ri-archive-line text-sm" /> Archive
            </button>
            {bulkBusy && <i className="ri-loader-4-line animate-spin text-base text-foreground-500" />}
          </div>
          <button
            type="button"
            disabled={bulkBusy}
            onClick={() => setSelected(new Set())}
            className="ml-auto cursor-pointer text-xs font-semibold text-foreground-600 transition-colors hover:text-foreground-900 disabled:opacity-60"
          >
            Clear selection
          </button>
        </div>
      )}

      <div className={adminCard}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-store-2-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700">No listings match your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] border-collapse">
              <thead className="border-b border-background-200">
                <tr>
                  <th className={`${adminTh} w-10`}>
                    <input
                      type="checkbox"
                      aria-label="Select all listings"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                      onChange={toggleAll}
                      className="h-3.5 w-3.5 cursor-pointer accent-primary-500"
                    />
                  </th>
                  <th className={adminTh}>Business</th>
                  <th className={adminTh}>Category / Location</th>
                  <th className={adminTh}>Owner</th>
                  <th className={adminTh}>Plan</th>
                  <th className={adminTh}>Status</th>
                  <th className={adminTh}>Views</th>
                  <th className={`${adminTh} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {filtered.map((b) => (
                  <tr key={b.id} className={`hover:bg-background-100/40 ${selected.has(b.id) ? "bg-primary-50/60" : ""}`}>
                    <td className={adminTd}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${b.name}`}
                        checked={selected.has(b.id)}
                        onChange={() => toggleRow(b.id)}
                        className="h-3.5 w-3.5 cursor-pointer accent-primary-500"
                      />
                    </td>
                    <td className={adminTd}>
                      <div className="flex items-center gap-2">
                        <a href={`/business/${b.slug}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground-900 hover:text-primary-700 hover:underline">
                          {b.name}
                        </a>
                        {b.isFeatured && <span title="Featured" className="flex h-4 w-4 items-center justify-center text-accent-600"><i className="ri-star-fill text-xs" /></span>}
                        {b.isVerified && <span title="Verified" className="flex h-4 w-4 items-center justify-center text-primary-600"><i className="ri-verified-badge-fill text-xs" /></span>}
                      </div>
                    </td>
                    <td className={adminTd}>
                      <p className="text-xs text-foreground-700">{b.categoryName}</p>
                      <p className="text-[11px] text-foreground-400">{b.townName ?? b.districtName}</p>
                    </td>
                    <td className={adminTd}>
                      <p className="text-xs text-foreground-700">{b.ownerName}</p>
                      <p className="text-[11px] text-foreground-400">{b.ownerEmail}</p>
                    </td>
                    <td className={adminTd}>
                      <span className="rounded-full bg-background-100 px-2 py-0.5 text-[10px] font-bold capitalize text-foreground-600">
                        {b.planSlug}
                      </span>
                    </td>
                    <td className={adminTd}>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${statusBadge(b.status)}`}>
                        {b.status}
                      </span>
                    </td>
                    <td className={`${adminTd} tabular-nums`}>{b.views.toLocaleString("en-IN")}</td>
                    <td className={`${adminTd} text-right`}>
                      <div className="flex items-center justify-end gap-1">
                        {b.status === "pending" && (
                          <button type="button" disabled={busyId === b.id} onClick={() => handleStatus(b, "active")} className={adminIconBtn} title="Approve & publish">
                            <i className="ri-checkbox-circle-line text-base text-emerald-600" />
                          </button>
                        )}
                        {b.status === "active" && (
                          <button type="button" disabled={busyId === b.id} onClick={() => handleStatus(b, "suspended")} className={adminIconBtn} title="Suspend">
                            <i className="ri-pause-circle-line text-base text-amber-600" />
                          </button>
                        )}
                        {(b.status === "rejected" || b.status === "suspended") && (
                          <button type="button" disabled={busyId === b.id} onClick={() => handleStatus(b, "active")} className={adminIconBtn} title="Re-activate">
                            <i className="ri-play-circle-line text-base text-emerald-600" />
                          </button>
                        )}
                        <button type="button" onClick={() => handleFlag(b, "isFeatured")} className={adminIconBtn} title={b.isFeatured ? "Remove from featured" : "Mark as featured"}>
                          <i className={`text-base ${b.isFeatured ? "ri-star-fill text-accent-500" : "ri-star-line"}`} />
                        </button>
                        <button type="button" onClick={() => handleFlag(b, "isVerified")} className={adminIconBtn} title={b.isVerified ? "Remove verified badge" : "Mark verified"}>
                          <i className={`text-base ${b.isVerified ? "ri-verified-badge-fill text-primary-600" : "ri-verified-badge-line"}`} />
                        </button>
                        {b.status === "pending" && (
                          <button type="button" disabled={busyId === b.id} onClick={() => { setAction({ type: "reject", business: b }); setReason(""); }} className={adminIconBtn} title="Reject">
                            <i className="ri-close-circle-line text-base text-rose-600" />
                          </button>
                        )}
                        <button type="button" onClick={() => setEditing(b)} className={adminIconBtn} title="Edit listing">
                          <i className="ri-edit-line text-base text-primary-600" />
                        </button>
                        <button type="button" onClick={() => setAction({ type: "delete", business: b })} className={adminIconBtn} title="Delete listing">
                          <i className="ri-delete-bin-line text-base text-rose-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reject modal */}
      {action?.type === "reject" && (
        <AdminModal
          title="Reject listing"
          subtitle={action.business.name}
          onClose={() => setAction(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAction(null)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={busyId === action.business.id} onClick={() => handleStatus(action.business, "rejected", reason.trim() || "Does not meet listing guidelines.")} className={adminBtnDanger}>
                Reject listing
              </button>
            </div>
          }
        >
          <label className={adminLabel} htmlFor="rej-reason">Reason (shared with the owner)</label>
          <textarea id="rej-reason" rows={3} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Business details could not be verified." className={`${adminInput} h-auto py-2.5`} />
        </AdminModal>
      )}

      {/* Delete modal */}
      {action?.type === "delete" && (
        <AdminModal
          title="Delete listing"
          subtitle={action.business.name}
          onClose={() => setAction(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAction(null)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={busyId === action.business.id} onClick={() => handleDelete(action.business)} className={adminBtnDanger}>
                <i className="ri-delete-bin-line text-sm" /> Delete permanently
              </button>
            </div>
          }
        >
          <p className="text-sm text-foreground-700">
            This will remove <strong>{action.business.name}</strong> from the directory. This action cannot be undone.
          </p>
        </AdminModal>
      )}

      {bulkConfirm === "archive" && (
        <AdminModal
          title="Archive listings"
          subtitle={`${selected.size} listing(s) selected`}
          onClose={() => setBulkConfirm(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setBulkConfirm(null)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={bulkBusy} onClick={() => runBulk("archive")} className={adminBtnDanger}>
                <i className="ri-archive-line text-sm" /> Archive listings
              </button>
            </div>
          }
        >
          <p className="text-sm text-foreground-700">
            {selected.size} listing(s) will be archived and hidden from the directory. They stay in the database
            (soft delete) so the change can be reversed server-side, but they will no longer be publicly visible.
          </p>
        </AdminModal>
      )}

      {(creating || editing) && (
        <BusinessFormModal
          mode={editing ? "edit" : "create"}
          token={accessToken}
          business={editing ?? undefined}
          existing={data}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={(row) => {
            if (editing) setData((prev) => prev.map((r) => (r.id === row.id ? row : r)));
            else setData((prev) => [row, ...prev]);
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      <p className="mt-4 text-xs text-foreground-500">
        <i className="ri-information-line mr-1" />
        Status and flag changes are enforced server-side by your API. Deletion is soft (archived) where the backend supports it.
      </p>
    </div>
  );
}